import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v3'
import { ArrowLeft, Save } from 'lucide-react'
import { toast } from 'sonner'

import type { CreatePurchaseInNoteRequest } from '@/lib/api/purchase-lot-api'
import type { LotOption } from '@/components/lots/lot-items-field'
import { purchaseLotApi } from '@/lib/api/purchase-lot-api'
import { StickyFormFooter } from '@/components/common/StickyFormFooter'
import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LotItemsField } from '@/components/lots/lot-items-field'
import { purchaseLotOption } from '@/components/lots/lot-option-builders'
import {
    ScanLotCodeButton,
    makeLotIdParser,
} from '@/components/lots/scan-lot-code-button'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    useCreatePurchaseInNote,
    usePurchaseLots,
} from '@/hooks/use-purchase-lot-query'
import { useTeaPurchasesList } from '@/hooks/use-tea-purchase-query'
import { useDebounced } from '@/hooks/use-debounced'
import { formatDateVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

// Khớp shape của LotItemsField: mỗi row là 'new' (lô mới) hoặc 'existing' (lô có sẵn).
// Lô mới ở kho lô chè mua BẮT BUỘC gắn 1 đơn mua — purchase_lots.tea_purchase_id
// là NOT NULL, không có lô "trần" như lô phân loại (chỉ cần grade).
//
// Item schema để LỎNG rồi validate trong superRefine, vì khi reason=from_purchase
// thì block items bị ẩn và giữ giá trị rỗng — validate cứng sẽ chặn submit oan.
const itemSchema = z.object({
    kind: z.enum(['new', 'existing']),
    purchase_lot_id: z.string().optional(),
    tea_purchase_id: z.string().optional(),
    new_lot_code: z.string().optional(),
    quantity: z.coerce.number().optional(),
})

const makeSchema = () =>
    z
        .object({
            reason: z.enum([
                'from_purchase',
                'adjustment',
                'return_from_sorting',
            ]),
            tea_purchase_id: z.string().optional(),
            notes: z.string().max(2000).optional(),
            items: z.array(itemSchema).optional(),
        })
        .superRefine((data, ctx) => {
            // from_purchase (cả tạo lẫn sửa): chọn đơn mua, hệ thống auto-split
            // lô — không dùng block items.
            if (data.reason === 'from_purchase') {
                if (!data.tea_purchase_id) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['tea_purchase_id'],
                        message: m.pl_pnk_tea_purchase(),
                    })
                }
                return
            }
            const items = data.items ?? []
            if (items.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['items'],
                    message: m.pl_pnk_items_required(),
                })
                return
            }
            items.forEach((it, i) => {
                if (it.kind === 'new') {
                    // BE cũng chặn (ErrNewLotOnlyAdjustment) — gác sớm ở FE cho rõ.
                    if (data.reason !== 'adjustment') {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['items', i, 'tea_purchase_id'],
                            message:
                                m.pl_pnk_item_err_new_lot_adjustment_only(),
                        })
                    } else if (!it.tea_purchase_id) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['items', i, 'tea_purchase_id'],
                            message: m.pl_pnk_item_err_pick_purchase(),
                        })
                    }
                } else if (!it.purchase_lot_id) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['items', i, 'purchase_lot_id'],
                        message: m.pl_pnk_item_err_pick_lot(),
                    })
                }
                if (!it.quantity || it.quantity <= 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['items', i, 'quantity'],
                        message: m.form_err_quantity_positive(),
                    })
                }
            })
        })

type FormData = z.infer<ReturnType<typeof makeSchema>>

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/purchase-lots/stock-in-notes/new',
)({
    component: Page,
})

function usePurchaseSearchDebounce(value: string, delay = 300) {
    const [debounced, setDebounced] = useState(value)
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay)
        return () => clearTimeout(t)
    }, [value, delay])
    return debounced
}

function Page() {
    const navigate = useNavigate()
    const create = useCreatePurchaseInNote()
    const [purchaseSearch, setPurchaseSearch] = useState('')
    const debouncedSearch = usePurchaseSearchDebounce(purchaseSearch)
    const [purchaseLotSearch, setPurchaseLotSearch] = useState('')
    const debouncedPurchaseLotSearch = useDebounced(purchaseLotSearch, 300)
    // from_purchase: chỉ đơn mua CHƯA có PNK (mỗi đơn mua tạo PNK đúng 1 lần).
    const { data: purchasesData } = useTeaPurchasesList({
        processed: true,
        has_active_pnk: false,
        per_page: 50,
        search: debouncedSearch || undefined,
    })
    // Lô mới khi điều chỉnh: đơn mua ĐÃ có PNK vẫn hợp lệ (đang thêm lô cho nó),
    // nên KHÔNG lọc has_active_pnk — nếu lọc thì list luôn rỗng.
    const { data: allPurchasesData } = useTeaPurchasesList({
        processed: true,
        per_page: 50,
        search: debouncedSearch || undefined,
    })
    // KHÔNG lọc only_available: PNK điều chỉnh / hoàn từ phân loại phải nhập
    // được vào cả lô đã xuất hết (quantity_available = 0).
    const { data: lotsData } = usePurchaseLots({
        per_page: 200,
        ...(debouncedPurchaseLotSearch
            ? { search: debouncedPurchaseLotSearch }
            : {}),
    })

    const teaPurchaseOptions = useMemo(
        () =>
            (purchasesData?.data ?? []).map((purchase) => ({
                value: purchase.id,
                label: `${purchase.code} — ${purchase.grower_name ?? ''} — ${formatDateVN(purchase.purchase_date)} — ${formatQty(purchase.dry_quantity ?? purchase.quantity)} kg`,
            })),
        [purchasesData],
    )
    const allTeaPurchaseOptions = useMemo(
        () =>
            (allPurchasesData?.data ?? []).map((purchase) => ({
                value: purchase.id,
                label: `${purchase.code} — ${purchase.grower_name ?? ''} — ${formatDateVN(purchase.purchase_date)} — ${formatQty(purchase.dry_quantity ?? purchase.quantity)} kg`,
            })),
        [allPurchasesData],
    )
    const lotOptions = useMemo(
        () => (lotsData?.data ?? []).map(purchaseLotOption),
        [lotsData],
    )

    // Lô quét mã có thể nằm ngoài top 200 của usePurchaseLots → Combobox không
    // thấy option và hiện placeholder dù form state đã đúng ID. Cache lại để inject.
    const [scannedLots, setScannedLots] = useState<Map<string, LotOption>>(
        new Map(),
    )
    const fetchAndCacheLot = async (lotId: string) => {
        if (scannedLots.has(lotId)) return
        try {
            const res = await purchaseLotApi.getLot(lotId)
            const l = res.data
            if (!l) return
            setScannedLots((prev) => {
                if (prev.has(lotId)) return prev
                const next = new Map(prev)
                next.set(lotId, purchaseLotOption(l))
                return next
            })
        } catch {
            // Bỏ qua — row vẫn giữ đúng ID trong form state.
        }
    }

    const handleSubmit = async (value: CreatePurchaseInNoteRequest) => {
        try {
            const res = await create.mutateAsync(value)
            const id = res.data?.id
            if (id) {
                void navigate({
                    to: '/purchase-lots/stock-in-notes/$id',
                    params: { id },
                })
            }
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    }

    return (
        <PurchaseLotStockInForm
            teaPurchaseOptions={teaPurchaseOptions}
            allTeaPurchaseOptions={allTeaPurchaseOptions}
            purchaseLotOptions={lotOptions}
            scannedLots={Array.from(scannedLots.values())}
            onLotScanned={(lotId) => void fetchAndCacheLot(lotId)}
            isPending={create.isPending}
            onPurchaseSearchChange={setPurchaseSearch}
            onPurchaseLotSearchChange={setPurchaseLotSearch}
            onCancel={() =>
                navigate({
                    to: '/purchase-lots/stock-in-notes',
                    search: { page: 1 },
                })
            }
            onSubmit={(value) => void handleSubmit(value)}
        />
    )
}

export function PurchaseLotStockInForm({
    mode = 'create',
    initialValues,
    title,
    submitLabel,
    teaPurchaseOptions,
    allTeaPurchaseOptions,
    purchaseLotOptions,
    scannedLots,
    onLotScanned,
    isPending,
    onPurchaseSearchChange,
    onPurchaseLotSearchChange,
    onSubmit,
    onCancel,
}: {
    mode?: 'create' | 'edit'
    initialValues?: FormData
    title?: string
    submitLabel?: string
    teaPurchaseOptions: Array<{ value: string; label: string }>
    allTeaPurchaseOptions: Array<{ value: string; label: string }>
    purchaseLotOptions: LotOption[]
    scannedLots?: LotOption[]
    onLotScanned?: (lotId: string) => void
    isPending: boolean
    onPurchaseSearchChange?: (v: string) => void
    onPurchaseLotSearchChange?: (v: string) => void
    onSubmit: (value: CreatePurchaseInNoteRequest) => void
    onCancel: () => void
}) {
    const isEdit = mode === 'edit'
    const form = useForm<FormData>({
        resolver: zodResolver(makeSchema()),
        defaultValues: initialValues ?? {
            reason: 'from_purchase',
            tea_purchase_id: '',
            notes: '',
            items: [
                {
                    kind: 'existing' as const,
                    purchase_lot_id: '',
                    quantity: 0,
                },
            ],
        },
    })
    const reason = useWatch({ control: form.control, name: 'reason' })
    // Chỉ PNK điều chỉnh mới được tạo lô mới. Hoàn từ phân loại phải trả kg về
    // lô đã tồn tại (BE cũng chặn: ErrNewLotOnlyAdjustment).
    const allowNewLot = reason === 'adjustment'

    // Đơn mua đã chọn tay được cache để nhãn không rớt khỏi trigger khi search
    // dùng chung đổi (server chỉ trả 50 dòng theo từ khoá hiện tại).
    const [pickedPurchase, setPickedPurchase] = useState<
        Map<string, { value: string; label: string }>
    >(new Map())
    const pickedTeaPurchaseId = form.watch('tea_purchase_id')
    const selectedTeaPurchase = pickedTeaPurchaseId
        ? pickedPurchase.get(pickedTeaPurchaseId)
        : undefined

    const submit = form.handleSubmit((data) => {
        // from_purchase (tạo & sửa): chọn đơn mua, BE auto-split lô.
        if (data.reason === 'from_purchase') {
            if (!data.tea_purchase_id) {
                toast.error(m.pl_pnk_tea_purchase())
                return
            }
            onSubmit({
                reason: 'from_purchase',
                tea_purchase_id: data.tea_purchase_id,
                notes: data.notes || undefined,
            })
            return
        }
        const lotItems = data.items ?? []
        if (lotItems.length === 0) {
            toast.error(m.pl_pnk_items_required())
            return
        }
        onSubmit({
            reason: data.reason,
            notes: data.notes || undefined,
            items: lotItems.map((item) =>
                item.kind === 'new'
                    ? {
                          tea_purchase_id: item.tea_purchase_id as string,
                          new_lot_code: item.new_lot_code || undefined,
                          quantity: Number(item.quantity),
                      }
                    : {
                          purchase_lot_id: item.purchase_lot_id as string,
                          quantity: Number(item.quantity),
                      },
            ),
        })
    })

    return (
        <div className="space-y-4 p-4 pb-24 md:p-6 md:pb-6">
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={onCancel}>
                    <ArrowLeft className="size-4 mr-1" />
                    {m.sl_lot_back()}
                </Button>
                <h1 className="text-xl font-semibold">
                    {title ?? m.pl_pnk_new_title()}
                </h1>
            </div>

            <FormProvider {...form}>
                <form onSubmit={submit} className="space-y-6 max-w-4xl">
                    <div className="grid gap-4 rounded-md border bg-card p-4 md:grid-cols-2">
                        <div>
                            <Label className="mb-1 block">
                                {m.pl_pnk_reason()}
                            </Label>
                            <Select
                                value={reason}
                                disabled={isEdit}
                                onValueChange={(value) =>
                                    form.setValue(
                                        'reason',
                                        value as FormData['reason'],
                                    )
                                }
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="from_purchase">
                                        {m.pl_in_reason_from_purchase()}
                                    </SelectItem>
                                    <SelectItem value="return_from_sorting">
                                        {m.pl_in_reason_return_from_sorting()}
                                    </SelectItem>
                                    <SelectItem value="adjustment">
                                        {m.pl_reason_adjustment()}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="mb-1 block">
                                {m.pl_pnk_notes()}
                            </Label>
                            <Input
                                {...form.register('notes')}
                                placeholder={m.sl_new_notes_placeholder()}
                            />
                        </div>
                    </div>

                    {reason === 'from_purchase' ? (
                        <div className="rounded-md border bg-card p-4">
                            <Label className="mb-1 block">
                                {m.pl_pnk_tea_purchase()}
                            </Label>
                            <Combobox
                                options={teaPurchaseOptions}
                                value={pickedTeaPurchaseId ?? ''}
                                onChange={(value) => {
                                    const opt = teaPurchaseOptions.find(
                                        (o) => o.value === value,
                                    )
                                    if (opt)
                                        setPickedPurchase((prev) => {
                                            if (prev.has(value)) return prev
                                            const next = new Map(prev)
                                            next.set(value, opt)
                                            return next
                                        })
                                    form.setValue('tea_purchase_id', value, {
                                        shouldValidate: true,
                                    })
                                }}
                                onSearchChange={onPurchaseSearchChange}
                                shouldFilter={false}
                                selectedOption={selectedTeaPurchase}
                                onOpenChange={
                                    onPurchaseSearchChange
                                        ? (open) => {
                                              if (!open)
                                                  onPurchaseSearchChange('')
                                          }
                                        : undefined
                                }
                                placeholder={m.pl_pnk_tea_purchase_placeholder()}
                                searchPlaceholder={m.pl_pnk_tea_purchase_search()}
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                                {m.pl_pnk_tea_purchase_hint()}
                            </p>
                        </div>
                    ) : (
                        <LotItemsField
                            mode="in"
                            lotOptions={purchaseLotOptions}
                            categoryOptions={allTeaPurchaseOptions}
                            categoryLabel={m.pl_pnk_tea_purchase()}
                            existingIdField="purchase_lot_id"
                            newCategoryIdField="tea_purchase_id"
                            onLotSearchChange={onPurchaseLotSearchChange}
                            onCategorySearchChange={onPurchaseSearchChange}
                            allowNewLot={allowNewLot}
                            lotPickerPlaceholder={m.pl_lot_picker_placeholder()}
                            scannedLots={scannedLots}
                            headerAction={
                                <ScanLotCodeButton
                                    parseLotId={makeLotIdParser(
                                        'purchase-lots/lots',
                                    )}
                                    onScanned={(lotId) => {
                                        // Quét luôn thêm row 'existing' — nhập
                                        // vào lô đã tồn tại trên tem.
                                        const current =
                                            form.getValues('items') ?? []
                                        form.setValue(
                                            'items',
                                            [
                                                ...current,
                                                {
                                                    kind: 'existing',
                                                    purchase_lot_id: lotId,
                                                    quantity: 0,
                                                },
                                            ],
                                            {
                                                shouldDirty: true,
                                                shouldValidate: false,
                                            },
                                        )
                                        onLotScanned?.(lotId)
                                    }}
                                />
                            }
                        />
                    )}

                    <StickyFormFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            onClick={onCancel}
                        >
                            {m.sl_new_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            disabled={isPending}
                        >
                            <Save className="size-4 mr-1" />
                            {submitLabel ?? m.pl_pnk_submit()}
                        </Button>
                    </StickyFormFooter>
                </form>
            </FormProvider>
        </div>
    )
}
